/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { forwardRef } from "react";
import { cn } from "@/shared/ui/utils/tailwind";
import { InfoCircledIcon } from "@/shared/ui/radix-icons";

export type InfoIconButtonProps = {
  onClick?: () => void;
  label?: string;
  title?: string;
  className?: string;
  'data-testid'?: string;
  'aria-expanded'?: boolean;
};

/**
 * Small circular info button used to surface didactic help next to a control.
 *
 * @param {InfoIconButtonProps} props - Info button properties.
 * @returns {JSX.Element} Rendered button.
 */
export const InfoIconButton = forwardRef<HTMLButtonElement, InfoIconButtonProps>(
  (
    {
      onClick,
      label = 'More information',
      title,
      className,
      'data-testid': testId,
      'aria-expanded': ariaExpanded,
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-expanded={ariaExpanded}
      title={title}
      data-testid={testId}
      className={cn(
        'inline-flex items-center justify-center rounded-full p-(--space-1)',
        'text-(--text-tertiary) transition-colors',
        'hover:text-(--seed-base) hover:bg-(--surface-secondary)',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-(--seed-base)',
        className,
      )}
    >
      <InfoCircledIcon className="h-4 w-4" />
    </button>
  ),
);

InfoIconButton.displayName = "InfoIconButton";

export default InfoIconButton;
